import { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../../services/api';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import DatePicker from '../../components/DatePicker';
import OnayPopup from '../../components/OnayPopup';
import UsageInfo from '../../components/UsageInfo';
import ModalShell from '../../components/ModalShell';
import CustomSelect from '../../components/CustomSelect';
import Buton from '../../components/Buton';
import { DeleteButton } from '../../components/ActionButtons';
import { useAuth } from '../../context/AuthContext';
import { useKasa } from '../../context/KasaContext';
import { rezMisafirAdi, rezIlkMisafirAdi, rezAramaMetni } from '../../utils/rezervasyon';
import { vergiAyristir } from '../../utils/vergi';

const INP = 'w-full px-5 py-3.5 rounded-2xl border-2 border-gray-100 text-xs font-bold text-gray-800 focus:border-violet-400 focus:ring-4 focus:ring-violet-100 outline-none transition-all';
const LBL = 'block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2.5 ml-1 leading-none';

const DURUM_RENK = {
  'Bekliyor': 'bg-amber-100 text-amber-700',
  'Tamamlandı': 'bg-emerald-100 text-emerald-700',
  'İptal': 'bg-slate-100 text-slate-500',
};

function bugunStr() {
  const d = new Date();
  return [String(d.getDate()).padStart(2, '0'), String(d.getMonth() + 1).padStart(2, '0'), d.getFullYear()].join('.');
}

function strToIso(s) {
  if (!s) return null;
  const [g, a, y] = s.split('.');
  return `${y}-${a}-${g}`;
}

function para(n, pb = 'TL') {
  return Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + pb;
}

// Havuz ekranıyla aynı kural: nakit dışı tahsilatta banka sorulur
function nakitMi(tip) {
  return !tip || tip.toLocaleLowerCase('tr-TR').includes('nakit');
}

/**
 * Otelde konaklayan misafiri seçtirir.
 *
 * Liste sayfa açılırken bir kez okunur; kutuya tıklanınca boşken de
 * konaklayanlar görünür. Misafir adı `rezervasyonMisafirler` içinden gelir —
 * rezervasyon DTO'sunda `misafirAdi` diye bir alan yoktur.
 */
function KonaklayanSecici({ konaklayanlar, secili, onSec, onKaldir }) {
  const [arama, setArama] = useState('');
  const [acik, setAcik] = useState(false);

  if (secili?.rezervasyonId) {
    return (
      <div className="flex items-center justify-between bg-white rounded-2xl px-5 py-3.5 border-2 border-indigo-100">
        <div>
          <p className="font-bold text-gray-700 text-sm">{secili.misafirAdi}</p>
          <p className="text-[10px] text-indigo-500 font-black uppercase tracking-widest mt-1">
            Otel misafiri · Oda {secili.odaNo || '—'}
          </p>
        </div>
        <button type="button" onClick={onKaldir}
          className="text-[10px] font-black text-rose-500 uppercase tracking-widest hover:text-rose-700">
          Kaldır
        </button>
      </div>
    );
  }

  const ara = arama.trim().toLocaleLowerCase('tr-TR');
  const sonuc = ara ? konaklayanlar.filter(r => rezAramaMetni(r).includes(ara)) : konaklayanlar;

  return (
    <div className="relative">
      <input className={INP} value={arama}
        onFocus={() => setAcik(true)}
        onBlur={() => setTimeout(() => setAcik(false), 150)}   // tıklama kaybolmasın
        onChange={e => { setArama(e.target.value); setAcik(true); }}
        disabled={konaklayanlar.length === 0}
        placeholder={konaklayanlar.length === 0
          ? 'Şu anda otelde konaklayan misafir yok'
          : 'Konaklayan misafiri seç (opsiyonel) — ad, oda no veya rez. no'} />

      {acik && sonuc.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-2 bg-white border border-gray-200 rounded-2xl divide-y divide-gray-100 max-h-72 overflow-y-auto shadow-xl">
          {sonuc.map(r => (
            <button key={r.rezervasyonId} type="button"
              className="w-full text-left px-4 py-2.5 hover:bg-indigo-50 text-sm"
              onClick={() => { onSec(r); setArama(''); setAcik(false); }}>
              <span className="font-bold text-gray-700">{rezMisafirAdi(r, '(isimsiz)')}</span>
              <span className="text-gray-400"> · Oda {r.odaNo || '—'} · {r.rezNo}</span>
              {r.durum !== 'C-IN Yapıldı' && (
                <span className="text-[10px] text-amber-500 font-bold"> · girişi yapılmadı</span>
              )}
            </button>
          ))}
        </div>
      )}

      {acik && ara && sonuc.length === 0 && (
        <p className="text-[10px] text-gray-400 mt-1.5">Eşleşen konaklayan misafir yok.</p>
      )}
    </div>
  );
}

export default function HizmetRandevulari() {
  const { hasYetkiOrAdmin } = useAuth();
  const { odemeTipleri, bankalar, dovizKurlari, refreshGelir } = useKasa();

  // Paket fiyatı dövizse tahsilatın TL karşılığı gösterilir (kasa TL tutuyor).
  const tlKarsiligi = (tutar, pb) => {
    if (!tutar || pb === 'TL' || pb === 'TRY') return null;
    const kur = dovizKurlari?.[pb];
    return kur ? parseFloat(tutar) * kur : null;
  };

  const [tarih, setTarih] = useState(bugunStr());
  const [tipler, setTipler] = useState([]);
  const [filtreTip, setFiltreTip] = useState('');
  const [randevular, setRandevular] = useState([]);
  const [konaklayanlar, setKonaklayanlar] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState('');
  const [mesaj, setMesaj] = useState('');

  const [form, setForm] = useState(null);          // yeni randevu formu
  const [doluluk, setDoluluk] = useState(null);
  const [rackDoluluk, setRackDoluluk] = useState(null);   // oda bazlı hizmetin gün planı
  const [odaDetay, setOdaDetay] = useState(null);         // açık oda kartının odası
  const [silOnay, setSilOnay] = useState(null);
  const [kaydediliyor, setKaydediliyor] = useState(false);

  const bildir = (m) => { setMesaj(m); setTimeout(() => setMesaj(''), 3500); };

  const tipleriGetir = useCallback(async () => {
    try { setTipler(await apiRequest('hizmet/tipler?sadeceAktif=true') || []); }
    catch { setTipler([]); }
  }, []);

  const randevulariGetir = useCallback(async () => {
    setYukleniyor(true); setHata('');
    try {
      const iso = strToIso(tarih);
      const q = new URLSearchParams({ baslangic: iso, bitis: iso });
      if (filtreTip) q.set('hizmetTipiId', filtreTip);
      setRandevular(await apiRequest(`hizmet/randevular?${q}`) || []);
    } catch (e) { setHata(e?.message || 'Randevular alınamadı.'); }
    finally { setYukleniyor(false); }
  }, [tarih, filtreTip]);

  useEffect(() => { tipleriGetir(); }, [tipleriGetir]);
  useEffect(() => { randevulariGetir(); }, [randevulariGetir]);

  // Konaklayanlar bir kez okunur; her tuş vuruşunda istek atılmaz.
  // Çıkışı yapılmış ve iptal olanlar dışarıda, otelde olanlar başta.
  useEffect(() => {
    apiRequest('rezervasyonlar')
      .then(d => {
        const uygun = (d || []).filter(r => r.durum !== 'İptal' && r.durum !== 'C-OUT Yapıldı');
        const icerde = uygun.filter(r => r.durum === 'C-IN Yapıldı');
        const digerleri = uygun.filter(r => r.durum !== 'C-IN Yapıldı');
        setKonaklayanlar([...icerde, ...digerleri]);
      })
      .catch(() => setKonaklayanlar([]));
  }, []);

  // Doluluk hizmete, tarihe ve pakete bağlı — slot adımı paketin süresidir
  useEffect(() => {
    if (!form?.hizmetTipiId) { setDoluluk(null); return; }
    const q = new URLSearchParams({ hizmetTipiId: form.hizmetTipiId, tarih: strToIso(form.tarih) });
    if (form.hizmetPaketiId) q.set('hizmetPaketiId', form.hizmetPaketiId);
    apiRequest(`hizmet/doluluk?${q}`).then(setDoluluk).catch(() => setDoluluk(null));
  }, [form?.hizmetTipiId, form?.hizmetPaketiId, form?.tarih]);

  // Filtrede oda bazlı bir hizmet seçiliyse ekran saat kutuları yerine
  // odaları oda planı gibi gösterir (RoomRack deseni).
  const rackTip = tipler.find(t => t.hizmetTipiId === Number(filtreTip));
  const rackAcik = !!rackTip?.odaBazliMi;

  useEffect(() => {
    if (!rackAcik) { setRackDoluluk(null); return; }
    const q = new URLSearchParams({ hizmetTipiId: filtreTip, tarih: strToIso(tarih) });
    apiRequest(`hizmet/doluluk?${q}`).then(setRackDoluluk).catch(() => setRackDoluluk(null));
  }, [rackAcik, filtreTip, tarih, randevular]);

  /** Bir odanın belirli slotunu kaplayan randevu — saatler "HH:mm", sıralama metin olarak doğru. */
  const slotRandevusu = useCallback((odaId, saat) => randevular.find(r =>
    r.hizmetOdasiId === odaId && r.durum !== 'İptal'
    && saat >= r.baslangicSaati && saat < r.bitisSaati), [randevular]);

  const seciliTip = tipler.find(t => t.hizmetTipiId === Number(form?.hizmetTipiId));
  const seciliPaket = seciliTip?.paketler?.find(p => p.hizmetPaketiId === Number(form?.hizmetPaketiId));
  const otelMisafiri = !!form?.rezervasyonId;

  // Oda bazlı hizmette saatler seçilen odanın planından okunur; aksi halde
  // ekrandaki müsaitlik hizmetin tamamını gösterir ve odayla örtüşmez.
  const odaBazli = !!seciliTip?.odaBazliMi;
  const odaSlotlari = odaBazli && form?.hizmetOdasiId
    ? doluluk?.odalar?.find(o => o.hizmetOdasiId === Number(form.hizmetOdasiId))?.saatler
    : null;
  const gosterilenSaatler = odaBazli ? (odaSlotlari || []) : (doluluk?.saatler || []);

  const birimFiyat = !seciliPaket ? 0
    : otelMisafiri ? (seciliPaket.misafirFiyat ?? 0)
    : seciliPaket.disFiyat;
  const ucretsiz = otelMisafiri && seciliPaket && seciliPaket.misafirFiyat == null;
  const toplam = birimFiyat * (Number(form?.kisiSayisi) || 1);

  // KDV oranı hizmet tanımından gelir; boşsa otel geneli oran raporda uygulanır
  const kdvOrani = seciliTip?.kdvOrani ?? null;
  const kdvTutar = (!ucretsiz && kdvOrani) ? vergiAyristir(toplam, kdvOrani, 0).kdv : null;

  const pesinTahsilat = !ucretsiz && toplam > 0 && form?.odemeSekli === 'pesin';
  const bankaGerekli = pesinTahsilat && !nakitMi(form?.odemeTipi) && bankalar.length > 0;

  const kaydedilebilir = form
    && form.hizmetPaketiId
    && form.baslangicSaati
    && (form.rezervasyonId || form.misafirAdi)
    && (!odaBazli || form.hizmetOdasiId)
    && (!pesinTahsilat || form.odemeTipi);

  // Rack'ten boş bir slota tıklanınca hizmet, oda ve saat hazır gelir.
  const formAc = (onDolu = {}) => setForm({
    hizmetTipiId: tipler[0]?.hizmetTipiId || '',
    hizmetPaketiId: '',
    hizmetOdasiId: '',
    tarih,
    baslangicSaati: '',
    misafirAdi: '',
    telefon: '',
    kisiSayisi: 1,
    odemeSekli: 'pesin',
    odemeTipi: odemeTipleri[0]?.ad || '',
    bankaAdi: '',
    notlar: '',
    rezervasyonId: null,
    odaNo: null,
    ...onDolu,
  });

  const kaydet = async () => {
    setKaydediliyor(true);
    try {
      await apiRequest('hizmet/randevular', 'POST', {
        hizmetTipiId: Number(form.hizmetTipiId),
        hizmetPaketiId: Number(form.hizmetPaketiId),
        hizmetOdasiId: odaBazli ? Number(form.hizmetOdasiId) : null,
        tarih: strToIso(form.tarih),
        baslangicSaati: form.baslangicSaati,
        rezervasyonId: form.rezervasyonId || null,
        misafirAdi: form.misafirAdi,
        telefon: form.telefon,
        kisiSayisi: Number(form.kisiSayisi) || 1,
        odemeSekli: form.odemeSekli,
        odemeTipi: pesinTahsilat ? form.odemeTipi : null,
        bankaAdi: bankaGerekli ? form.bankaAdi : '',
        notlar: form.notlar,
      });
      setForm(null);
      bildir('Randevu oluşturuldu.');
      randevulariGetir();
      if (pesinTahsilat) refreshGelir?.();   // kasa ekranları tazelensin
    } catch (e) { setHata(e?.message || 'Randevu oluşturulamadı.'); }
    finally { setKaydediliyor(false); }
  };

  const durumDegistir = async (id, durum) => {
    try {
      await apiRequest(`hizmet/randevular/${id}/durum?durum=${encodeURIComponent(durum)}`, 'PATCH');
      randevulariGetir();
      if (durum === 'İptal') refreshGelir?.();
    } catch (e) { setHata(e?.message || 'Durum güncellenemedi.'); }
  };

  const sil = async () => {
    try {
      await apiRequest(`hizmet/randevular/${silOnay}`, 'DELETE');
      setSilOnay(null); bildir('Randevu silindi.'); randevulariGetir(); refreshGelir?.();
    } catch (e) { setSilOnay(null); setHata(e?.message || 'Silinemedi.'); }
  };

  const odemeMetni = (r) => {
    if (r.ucretsizMi) return 'misafire dahil';
    if (r.odemeSekli === 'odaHesabi') return 'oda hesabına';
    return r.odemeTipi ? `peşin · ${r.odemeTipi.toLocaleLowerCase('tr-TR')}` : 'peşin';
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <PageHeader
        icon="🗓️"
        iconColor="from-violet-600 to-fuchsia-700"
        iconShadow="shadow-violet-200"
        title="Hizmet Randevuları"
        subtitle="Hamam, sauna, masaj ve diğer ek hizmetler"
      >
        <Buton renk="violet" disabled={tipler.length === 0} onClick={formAc}>
          + Yeni Randevu
        </Buton>
      </PageHeader>

      {/* Kurulum yönergesi yalnızca tanım yetkisi olana gösterilir; resepsiyonist
          gidemeyeceği bir sayfaya yönlendirilmemeli. */}
      {tipler.length === 0 && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4 text-sm font-semibold text-amber-700">
          {hasYetkiOrAdmin('tanimlarHizmet')
            ? <>Henüz aktif hizmet tanımı yok. Önce <strong>Otel Kurulumu → Ek Hizmetler</strong> sayfasından paket ve fiyatları girin.</>
            : <>Randevu alınabilecek bir hizmet tanımlı değil. Yöneticinizle görüşün.</>}
        </div>
      )}
      {hata && <div className="rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 px-5 py-4 text-sm font-semibold">{hata}</div>}
      {mesaj && <div className="rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-700 px-5 py-4 text-sm font-semibold">{mesaj}</div>}

      {/* Filtreler */}
      <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm p-4 md:p-5 flex flex-wrap items-end gap-4">
        <div>
          <label className={LBL}>Tarih</label>
          <DatePicker value={tarih} onChange={setTarih} />
        </div>
        <div className="min-w-[220px]">
          <label className={LBL}>Hizmet</label>
          <CustomSelect value={filtreTip} onChange={setFiltreTip} placeholder="Tümü"
            options={tipler.map(t => ({ deger: t.hizmetTipiId, etiket: t.ad }))} />
        </div>
      </div>

      {/* Oda planı — sauna, SPA, tuz odası gibi kabinli hizmetlerde odalar
          otel oda planı gibi gösterilir; boş saate tıklayınca randevu açılır. */}
      {rackAcik && (
        <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm p-5 md:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div>
              <h3 className="text-lg font-black text-slate-800 flex items-center gap-2">
                <span className="font-emoji">{rackTip.ikon || '🧖'}</span> {rackTip.ad} · Oda Planı
              </h3>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {tarih} · {rackTip.acilisSaati}-{rackTip.kapanisSaati}
                {rackDoluluk && ` · ${rackDoluluk.sureDk} dk seans`}
              </p>
            </div>
            <Buton renk="violet" className="!px-5 !py-2.5"
              onClick={() => formAc({ hizmetTipiId: rackTip.hizmetTipiId })}>
              + Randevu
            </Buton>
          </div>

          {!rackDoluluk ? (
            <LoadingSpinner color="violet" text="Oda planı yükleniyor…" />
          ) : rackDoluluk.odalar.length === 0 ? (
            <EmptyState icon="🚪"
              text={hasYetkiOrAdmin('tanimlarHizmet')
                ? 'Bu hizmete oda tanımlanmamış. Tanımlar → Ek Hizmetler sayfasından oda ekleyin.'
                : 'Bu hizmete oda tanımlanmamış. Yöneticinizle görüşün.'} />
          ) : (
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
              {rackDoluluk.odalar.map(o => {
                const doluSlot = o.saatler.filter(s => !s.musait).length;
                const odaRandevulari = randevular
                  .filter(r => r.hizmetOdasiId === o.hizmetOdasiId && r.durum !== 'İptal')
                  .sort((a, b) => a.baslangicSaati.localeCompare(b.baslangicSaati));
                const bos = doluSlot === 0;

                return (
                  <button key={o.hizmetOdasiId} type="button" onClick={() => setOdaDetay(o)}
                    className={`text-left rounded-2xl border-2 overflow-hidden transition hover:shadow-md active:scale-[0.99] ${
                      bos ? 'border-emerald-100 hover:border-emerald-300' : 'border-violet-100 hover:border-violet-300'}`}>
                    <div className={`px-4 py-3 ${bos ? 'bg-emerald-500' : 'bg-violet-600'}`}>
                      <p className="font-black text-white truncate">{o.ad}</p>
                      <p className="text-[10px] font-bold text-white/70 uppercase tracking-widest">
                        {o.kisiKapasitesi} kişi · {doluSlot}/{o.saatler.length} dolu
                      </p>
                    </div>

                    <div className="p-3 space-y-1.5 min-h-[92px]">
                      {odaRandevulari.length === 0 ? (
                        <p className="text-xs text-slate-400 font-semibold py-4 text-center">Gün boyu boş</p>
                      ) : (
                        <>
                          {odaRandevulari.slice(0, 3).map(r => (
                            <div key={r.hizmetRandevuId} className="flex items-center gap-2 text-xs">
                              <span className="font-black text-slate-700 shrink-0">{r.baslangicSaati}</span>
                              <span className="text-slate-500 truncate">{r.misafirAdi}</span>
                            </div>
                          ))}
                          {odaRandevulari.length > 3 && (
                            <p className="text-[10px] font-bold text-slate-400">+{odaRandevulari.length - 3} randevu daha</p>
                          )}
                        </>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Liste */}
      <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
        {yukleniyor ? <LoadingSpinner color="violet" text="Randevular yükleniyor…" />
          : randevular.length === 0 ? <EmptyState icon="🗓️" text="Bu tarihte randevu yok." />
          : (
            <div className="divide-y divide-slate-100">
              {randevular.map(r => (
                <div key={r.hizmetRandevuId} className="px-5 py-4 flex flex-wrap items-center gap-4 hover:bg-slate-50/60">
                  <div className="text-center min-w-[86px]">
                    <p className="text-lg font-black text-slate-800">{r.baslangicSaati}</p>
                    <p className="text-[10px] font-bold text-slate-400">{r.bitisSaati}</p>
                  </div>

                  <div className="flex-1 min-w-[180px]">
                    <p className="font-black text-slate-700 flex items-center gap-1.5">
                      <span className="font-emoji">{r.ikon || '🧖'}</span> {r.hizmetAdi}
                      <span className="text-slate-400 font-bold">· {r.paketAdi}</span>
                      {r.odaAdi && (
                        <span className="px-2 py-0.5 rounded-lg bg-violet-50 text-violet-600 text-[10px] font-black uppercase tracking-widest">
                          {r.odaAdi}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {r.misafirAdi}
                      {r.kisiSayisi > 1 && ` · ${r.kisiSayisi} kişi`}
                      {r.telefon && ` · ${r.telefon}`}
                    </p>
                  </div>

                  <div className="min-w-[150px]">
                    {r.otelMisafiriMi ? (
                      <span className="inline-block px-3 py-1 rounded-lg bg-indigo-50 text-indigo-600 text-[10px] font-black uppercase tracking-widest">
                        Oda {r.odaNo || '—'}
                      </span>
                    ) : (
                      <span className="inline-block px-3 py-1 rounded-lg bg-slate-100 text-slate-500 text-[10px] font-black uppercase tracking-widest">
                        Dışarıdan
                      </span>
                    )}
                  </div>

                  <div className="text-right min-w-[130px]">
                    {r.ucretsizMi
                      ? <p className="font-black text-emerald-600">Ücretsiz</p>
                      : <p className="font-black text-slate-700">{para(r.tutar, r.paraBirimi)}</p>}
                    <p className="text-[10px] font-bold text-slate-400">{odemeMetni(r)}</p>
                  </div>

                  <span className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest ${DURUM_RENK[r.durum] || ''}`}>
                    {r.durum}
                  </span>

                  <div className="flex gap-2">
                    {r.durum === 'Bekliyor' && (
                      <>
                        <button onClick={() => durumDegistir(r.hizmetRandevuId, 'Tamamlandı')}
                          className="px-3 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-600 text-emerald-600 hover:text-white text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 border border-emerald-100">Tamamla</button>
                        <button onClick={() => durumDegistir(r.hizmetRandevuId, 'İptal')}
                          className="px-3 py-1.5 rounded-xl bg-slate-50 hover:bg-slate-500 text-slate-500 hover:text-white text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 border border-slate-100">İptal</button>
                      </>
                    )}
                    <DeleteButton onClick={() => setSilOnay(r.hizmetRandevuId)} />
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      {/* Oda gün planı — boş slota tıklayınca randevu formu oda ve saatle açılır */}
      {odaDetay && (
        <ModalShell
          icon="🚪"
          iconColor="from-violet-600 to-fuchsia-700"
          title={odaDetay.ad}
          subtitle={`${rackTip?.ad || ''} · ${tarih} · ${odaDetay.kisiKapasitesi} kişi`}
          size="lg"
          onClose={() => setOdaDetay(null)}
          footer={<Buton tur="ikincil" onClick={() => setOdaDetay(null)}>Kapat</Buton>}>

          <div>
            <label className={LBL}>Gün Planı</label>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {odaDetay.saatler.map(s => {
                const r = slotRandevusu(odaDetay.hizmetOdasiId, s.saat);
                return (
                  <button key={s.saat} type="button" disabled={!!r}
                    onClick={() => {
                      setOdaDetay(null);
                      formAc({
                        hizmetTipiId: rackTip.hizmetTipiId,
                        hizmetOdasiId: odaDetay.hizmetOdasiId,
                        baslangicSaati: s.saat,
                      });
                    }}
                    className={`py-2.5 rounded-xl text-xs font-black transition ${
                      r ? 'bg-violet-100 text-violet-700 cursor-default'
                        : 'bg-gray-100 text-gray-600 hover:bg-emerald-100 hover:text-emerald-700'}`}>
                    {s.saat}
                    <span className="block text-[9px] font-bold opacity-70 truncate px-1">
                      {r ? r.misafirAdi : 'boş'}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-gray-400 mt-2">Boş bir saate tıklayarak randevu oluşturabilirsiniz.</p>
          </div>

          {/* Odanın o günkü randevuları ve işlemleri */}
          <div>
            <label className={LBL}>Bu Odanın Randevuları</label>
            {randevular.filter(r => r.hizmetOdasiId === odaDetay.hizmetOdasiId).length === 0 ? (
              <p className="text-sm text-gray-400">Bu odada bugün randevu yok.</p>
            ) : (
              <div className="divide-y divide-slate-100 border border-slate-100 rounded-2xl overflow-hidden">
                {randevular
                  .filter(r => r.hizmetOdasiId === odaDetay.hizmetOdasiId)
                  .sort((a, b) => a.baslangicSaati.localeCompare(b.baslangicSaati))
                  .map(r => (
                    <div key={r.hizmetRandevuId} className="px-4 py-3 flex flex-wrap items-center gap-3">
                      <span className="font-black text-slate-700 min-w-[86px]">{r.baslangicSaati}-{r.bitisSaati}</span>
                      <div className="flex-1 min-w-[140px]">
                        <p className="font-bold text-slate-700 text-sm">{r.misafirAdi}</p>
                        <p className="text-[11px] text-slate-400">
                          {r.paketAdi}{r.kisiSayisi > 1 && ` · ${r.kisiSayisi} kişi`} · {odemeMetni(r)}
                        </p>
                      </div>
                      <span className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest ${DURUM_RENK[r.durum] || ''}`}>
                        {r.durum}
                      </span>
                      {r.durum === 'Bekliyor' && (
                        <div className="flex gap-2">
                          <button onClick={() => durumDegistir(r.hizmetRandevuId, 'Tamamlandı')}
                            className="px-3 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-600 text-emerald-600 hover:text-white text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 border border-emerald-100">Tamamla</button>
                          <button onClick={() => durumDegistir(r.hizmetRandevuId, 'İptal')}
                            className="px-3 py-1.5 rounded-xl bg-slate-50 hover:bg-slate-500 text-slate-500 hover:text-white text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 border border-slate-100">İptal</button>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </ModalShell>
      )}

      {/* Yeni randevu */}
      {form && (
        <ModalShell
          icon="🗓️"
          iconColor="from-violet-600 to-fuchsia-700"
          title="Yeni Randevu"
          subtitle="Hizmet, saat ve tahsilat bilgileri"
          size="xl"
          onClose={() => setForm(null)}
          footer={
            <>
              <Buton tur="ikincil" onClick={() => setForm(null)}>Vazgeç</Buton>
              <Buton renk="violet" onClick={kaydet} disabled={!kaydedilebilir || kaydediliyor}>
                {kaydediliyor ? 'Kaydediliyor…' : '💾 Randevu Oluştur'}
              </Buton>
            </>
          }>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={LBL}>Hizmet *</label>
              <CustomSelect value={form.hizmetTipiId}
                onChange={v => setForm({ ...form, hizmetTipiId: v, hizmetPaketiId: '', hizmetOdasiId: '', baslangicSaati: '' })}
                options={tipler.map(t => ({ deger: t.hizmetTipiId, etiket: t.ad }))} />
            </div>
            <div>
              <label className={LBL}>Paket *</label>
              <CustomSelect value={form.hizmetPaketiId} placeholder="— Seçiniz —"
                onChange={v => setForm({ ...form, hizmetPaketiId: v, baslangicSaati: '' })}
                options={(seciliTip?.paketler || []).filter(p => p.aktifMi).map(p => ({
                  deger: p.hizmetPaketiId,
                  etiket: `${p.ad} — ${para(p.disFiyat, p.paraBirimi)}`,
                }))} />
            </div>
          </div>

          {/* Oda bazlı hizmette randevu bir odaya yazılır; müsaitlik de o odaya göre gösterilir. */}
          {odaBazli && (
            <div>
              <label className={LBL}>Oda *</label>
              <CustomSelect value={form.hizmetOdasiId} placeholder="— Oda seçiniz —"
                onChange={v => setForm({ ...form, hizmetOdasiId: v, baslangicSaati: '' })}
                options={(seciliTip.odalar || []).filter(o => o.aktifMi).map(o => ({
                  deger: o.hizmetOdasiId,
                  etiket: `${o.ad} (${o.kisiKapasitesi} kişi)`,
                }))} />
              {(seciliTip.odalar || []).filter(o => o.aktifMi).length === 0 && (
                <p className="text-[10px] font-bold text-amber-600 mt-1.5">
                  Bu hizmete randevuya açık oda tanımlanmamış. Tanımlar → Ek Hizmetler sayfasından oda ekleyin.
                </p>
              )}
            </div>
          )}

          {/* Müşteri */}
          <div className="bg-gray-50 rounded-2xl p-5 space-y-4">
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Müşteri</p>

            <div>
              <label className={LBL}>Otelde konaklayan misafir (opsiyonel)</label>
              <KonaklayanSecici
                konaklayanlar={konaklayanlar}
                secili={form}
                onSec={r => setForm({
                  ...form,
                  rezervasyonId: r.rezervasyonId,
                  odaNo: r.odaNo,
                  misafirAdi: rezIlkMisafirAdi(r),
                  odemeSekli: 'odaHesabi',
                })}
                onKaldir={() => setForm({ ...form, rezervasyonId: null, odaNo: null, misafirAdi: '', odemeSekli: 'pesin' })}
              />
              <p className="text-[10px] text-gray-400 mt-1.5">
                Seçilirse misafir fiyatı uygulanır ve oda hesabına yazılabilir. Boş bırakılırsa dışarıdan gelen müşteri sayılır.
              </p>
            </div>

            {!form.rezervasyonId && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={LBL}>Ad Soyad *</label>
                  <input className={INP} value={form.misafirAdi} onChange={e => setForm({ ...form, misafirAdi: e.target.value })} />
                </div>
                <div>
                  <label className={LBL}>Telefon</label>
                  <input className={INP} value={form.telefon} onChange={e => setForm({ ...form, telefon: e.target.value })} />
                </div>
              </div>
            )}
          </div>

          {/* Zaman ve kişi */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={LBL}>Tarih *</label>
              <DatePicker value={form.tarih} onChange={v => setForm({ ...form, tarih: v, baslangicSaati: '' })} />
            </div>
            <div>
              <label className={LBL}>Kişi Sayısı</label>
              <input type="number" min="1" className={INP + ' text-center'} value={form.kisiSayisi}
                onChange={e => setForm({ ...form, kisiSayisi: parseInt(e.target.value) || 1 })} />
            </div>
          </div>

          {/* Saat seçimi */}
          <div>
            <label className={LBL}>
              Saat *
              {doluluk && (
                <span className="text-gray-400 normal-case font-bold tracking-normal">
                  {' '}— {doluluk.sureDk} dk seans
                  {odaBazli ? ', seçili odanın müsaitliği' : `, kapasite ${doluluk.kapasite}`}
                </span>
              )}
            </label>
            {!doluluk ? (
              <p className="text-sm text-gray-400">Hizmet seçin…</p>
            ) : odaBazli && !form.hizmetOdasiId ? (
              <p className="text-sm text-gray-400">Önce oda seçin…</p>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                {gosterilenSaatler.map(s => {
                  // Kalan kapasite yalnızca doluluk başladığında anlamlı; boş
                  // saatlerde "N yer" yazmak her kutuya aynı sayıyı basıyordu.
                  // Oda bazlıda slot ya boş ya dolu, kalan kapasite anlamsız.
                  const kismenDolu = !odaBazli && s.musait && s.kalanKapasite < doluluk.kapasite;
                  return (
                    <button key={s.saat} disabled={!s.musait}
                      onClick={() => setForm({ ...form, baslangicSaati: s.saat })}
                      className={`py-2.5 rounded-xl text-xs font-black transition ${
                        form.baslangicSaati === s.saat ? 'bg-violet-600 text-white'
                        : s.musait ? 'bg-gray-100 text-gray-600 hover:bg-violet-100'
                        : 'bg-rose-50 text-rose-300 cursor-not-allowed'}`}>
                      {s.saat}
                      {!s.musait && <span className="block text-[9px] font-bold">Dolu</span>}
                      {kismenDolu && <span className="block text-[9px] font-bold opacity-70">{s.kalanKapasite} yer</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Tahsilat */}
          <div className="bg-gray-50 rounded-2xl p-5 space-y-4">
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Tahsilat</p>

            {ucretsiz ? (
              <p className="text-sm font-semibold text-emerald-600">
                Bu paket otel misafirine ücretsiz sunuluyor; tahsilat yapılmaz.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={LBL}>Ödeme Şekli</label>
                    <CustomSelect value={form.odemeSekli} disabled={!otelMisafiri}
                      onChange={v => setForm({ ...form, odemeSekli: v })}
                      options={otelMisafiri
                        ? [{ deger: 'odaHesabi', etiket: 'Oda hesabına' }, { deger: 'pesin', etiket: 'Peşin' }]
                        : [{ deger: 'pesin', etiket: 'Peşin' }]} />
                    {!otelMisafiri && (
                      <p className="text-[10px] text-gray-400 mt-1.5">Dışarıdan gelen müşteri oda hesabına yazdıramaz.</p>
                    )}
                  </div>

                  {form.odemeSekli === 'pesin' && (
                    <div>
                      <label className={LBL}>Ödeme Tipi *</label>
                      <CustomSelect value={form.odemeTipi} placeholder="Seçiniz"
                        onChange={v => setForm({ ...form, odemeTipi: v, bankaAdi: nakitMi(v) ? '' : form.bankaAdi })}
                        options={odemeTipleri.map(o => ({ deger: o.ad, etiket: o.ad }))} />
                    </div>
                  )}

                  {bankaGerekli && (
                    <div>
                      <label className={LBL}>Banka</label>
                      <CustomSelect value={form.bankaAdi} placeholder="Seçiniz"
                        onChange={v => setForm({ ...form, bankaAdi: v })}
                        options={bankalar.map(b => ({ deger: b.ad, etiket: b.ad }))} />
                    </div>
                  )}
                </div>

                {tlKarsiligi(toplam, seciliPaket?.paraBirimi) !== null && (
                  <p className="text-[10px] font-black text-violet-600 bg-violet-50 px-3 py-2 rounded-xl inline-flex uppercase tracking-widest">
                    ≈ {tlKarsiligi(toplam, seciliPaket.paraBirimi).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                    <span className="opacity-60 ml-1.5">(1 {seciliPaket.paraBirimi} = {dovizKurlari[seciliPaket.paraBirimi]} ₺)</span>
                  </p>
                )}

                {form.odemeSekli === 'odaHesabi' && (
                  <p className="text-[11px] text-gray-500">
                    Tutar rezervasyonun adisyonuna yazılır, tahsilat check-out sırasında yapılır.
                  </p>
                )}
              </>
            )}
          </div>

          <div>
            <label className={LBL}>Not</label>
            <input className={INP} value={form.notlar} onChange={e => setForm({ ...form, notlar: e.target.value })} />
          </div>

          {/* Tutar özeti */}
          {seciliPaket && (
            <div className="bg-violet-50 border border-violet-100 rounded-2xl px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-violet-500">Tutar</p>
                <p className="text-[11px] text-gray-500 mt-1">
                  {otelMisafiri ? 'Otel misafiri fiyatı' : 'Dışarıdan gelen fiyatı'}
                  {form.kisiSayisi > 1 && ` · ${form.kisiSayisi} kişi`}
                  {kdvTutar !== null && ` · KDV %${kdvOrani} (${para(kdvTutar, seciliPaket.paraBirimi)})`}
                </p>
              </div>
              <p className="text-2xl font-black text-violet-700">
                {ucretsiz ? 'Ücretsiz' : para(toplam, seciliPaket.paraBirimi)}
              </p>
            </div>
          )}
        </ModalShell>
      )}

      <OnayPopup
        acik={!!silOnay}
        baslik="Randevuyu Sil"
        mesaj="Randevu silinecek. Peşin tahsilatı yapıldıysa kasa kaydı da geri alınır. Oda hesabına yazılmış ve ödenmiş bir adisyon varsa silinemez."
        onEvet={sil}
        onHayir={() => setSilOnay(null)}
      />

      <UsageInfo
        color="violet"
        description="Ek hizmetler için randevu oluşturur ve günlük takibini yaparsınız. Saat seçerken kapasiteye göre dolu saatler kapalı görünür."
        scenarios={[
          { title: 'Otel misafiri mi, dışarıdan mı?', content: 'Konaklayan misafiri arayıp seçerseniz misafir fiyatı uygulanır ve ödeme oda hesabına yazdırılabilir. Seçmezseniz dışarıdan gelen kabul edilir; dış fiyat geçerli olur ve tahsilat peşin alınır.' },
          { title: 'Peşin tahsilat', content: 'Ödeme tipi (nakit, kredi kartı, havale) seçilir ve tutar aynı anda kasaya gelir olarak yazılır. Nakit dışı tahsilatta banka da sorulur. Randevu iptal edilir veya silinirse bu kasa kaydı geri alınır.' },
          { title: 'Oda hesabına yazma', content: 'Sistem otomatik olarak adisyon kaydı açar; tutar check-out hesabına eklenir ve konaklama vergisi raporuna dahil olur.' },
          { title: 'Ücretsiz hizmetler', content: 'Pakette otel misafiri fiyatı boş bırakılmışsa hizmet misafire ücretsiz sunulur; randevu yine kaydedilir, kapasiteden düşer.' },
          { title: 'Kapasite dolduğunda', content: 'Aynı saatte kapasite kadar randevu varsa o saat seçilemez. Kapasiteyi hizmet tanımından değiştirebilirsiniz.' },
          { title: 'Oda planı', content: 'Sauna, SPA ve tuz odası gibi oda bazlı hizmetlerde yukarıdaki Hizmet filtresinden o hizmeti seçin: odalar otel oda planı gibi listelenir. Bir odaya tıklayınca gün planı açılır, boş saate tıklayınca randevu formu oda ve saatle hazır gelir. Bir odaya aynı saatte tek randevu girer.' },
        ]}
      />
    </div>
  );
}
